import React, { useRef, useLayoutEffect, useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Send, Square, X, Mic, Wallet, Check, Loader2, Eye, EyeOff, Plus, Upload, FolderOpen, Pencil, ChevronDown, ChevronUp } from 'lucide-react';
import AttachmentPill from './AttachmentPill';
import VoiceClipPlayer from './VoiceClipPlayer';
import { useLanguage } from '../i18n';
import { pickPlaceholder } from '../utils/composerPlaceholder';

// Composer auto-grow bounds. The textarea height is driven PURELY by its content:
// it grows one line at a time as the user types OR pastes, caps at MAX and then
// shows an internal scrollbar, and snaps back to MIN (a single line) the
// moment the content clears (after send) or the value changes on a conversation
// switch. There is no persistent manual height, so the box can never get "stuck"
// large or locked to a single line (Type box size ticket).
const MIN_COMPOSER_HEIGHT = 48;   // one line + padding
// The "+" apex layout stacks [+] over [Mic][Send] (~84px). Only a box at least this
// tall (3 lines) can hold that stack; a 2-line box (a narrow chat wrapping its
// placeholder) keeps the horizontal row, or the apex escapes above the border (#492).
const TRIANGLE_MIN_HEIGHT = 96;
// arrow box (#313, RE-OPENED): the cap was cut by 20% (300 → 240). The composer is
// only one of several stacked blocks above the fold — attachments, replies and the
// voice tray all sit above it — so a text box that could eat 300px of a short
// window was the single biggest contributor to the composer pushing the thread off
// screen. Past the cap the textarea scrolls internally exactly as before.
const MAX_COMPOSER_HEIGHT = 240;  // ≈9 lines, then internal scroll (was 300, −20%)

// ── Voice segment tray sizing (arrow box #313 re-open, points 2 + 3) ──────────
//
// (2) The tray NEVER grows with the number of clips: it is a fixed-cap scroller, so
// 3 clips and 100 clips occupy the same maximum space.
const SEGMENT_TRAY_MAX_HEIGHT = 240;
// (3) THE 50% RULE: once the text box has grown past half its (new) cap, the tray
// collapses to the height of a SINGLE audio row and scrolls, so a long message
// plus many recordings still cannot push the composer off screen.
//
// The collapsed height is a CONSTANT derived from a clip's fixed chrome — the
// 36px play button + 2×8px player padding + its 2px border + the 4px column gap
//     + the ~18px status line ≈ 76px — deliberately NOT a measurement of the first
// chip. A measured height would balloon with the number of clips, defeating the
// bound this exists to guarantee.
//
// arrow box (#313, 2nd RE-OPEN — "High-Pressure Input & Transcription
// Refinements"): the collapse now YIELDS TO A REVEALED TRANSCRIPTION. The
// original rule ("a clip whose transcript is open simply scrolls") was the root
// cause of BOTH reported issues: at the composer cap the tray is 76px — barely
// one audio row — so the editor that "Show transcription" reveals is rendered
// BELOW that viewport, i.e. the click produced no visible feedback at all, and
// the editor's expand/collapse arrow (pinned to the editor's own top-right) went
// out of reach with it. So the 50% rule applies only while NO transcription is
// open; the moment one is, the tray reverts to its normal cap. The #313
// invariant is untouched — with nothing revealed, 3 clips and 100 clips still
// occupy the same bounded space, and the tray is still a capped scroller in
// BOTH states (max-height, so it only ever renders as tall as its content).
const SEGMENT_TRAY_COLLAPSED_HEIGHT = 76;
const COMPOSER_TALL_RATIO = 0.5;

/**
 * Bring a just-revealed / just-resized transcription box into view inside every
 * scrollable ancestor (the segment tray above all), without moving the page more
 * than strictly necessary — `block: 'nearest'` scrolls each ancestor by the
 * minimum, and when the element is TALLER than its scrollport it aligns start-to-
 * start, which puts the editor's top edge (and therefore its toggle arrow) at the
 * top of the tray.
 *
 * Fired twice on purpose: once on the next frame (immediate feedback) and once
 * after the tray's 150ms max-height transition has settled, because a scroll
 * computed against the mid-transition box would be measured against a viewport
 * that is still growing.
 */
const scrollTranscriptIntoView = (el) => {
    if (!el) return () => {};
    const run = () => {
        try { el.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch (_) { /* older engines */ }
    };
    const raf = requestAnimationFrame(run);
    const timer = setTimeout(run, 200); // > the tray's 150ms max-height transition
    return () => { cancelAnimationFrame(raf); clearTimeout(timer); };
};

const formatExpenseAmount = (amount, currency = 'USD') => {
    if (amount == null) return '';
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currency || 'USD',
            maximumFractionDigits: 2,
        }).format(amount);
    } catch (_) {
        return `${currency || 'USD'} ${Number(amount).toFixed(2)}`;
    }
};

const ExpenseReferenceChip = ({ expense, onRemove }) => {
    const { t } = useLanguage();
    return (
    <div className="flex items-center gap-2 px-2 py-1 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg text-xs">
        <Wallet size={14} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
        <span className="font-semibold text-emerald-700 dark:text-emerald-300">
            {formatExpenseAmount(expense.amount, expense.currency)}
        </span>
        <span className="text-pai-secondary truncate max-w-[120px]">
            {expense.merchant || t('composerExpenseFallback', 'Expense')}
        </span>
        {expense.book_name && (
            <span className="text-[10px] text-pai-meta">· {expense.book_name}</span>
        )}
        <button
            onClick={() => onRemove?.(expense.expense_id)}
            className="ml-0.5 p-0.5 text-pai-faint hover:text-pai-secondary rounded"
            title={t('composerRemoveReference', 'Remove reference')}
        >
            <X size={12} />
        </button>
    </div>
    );
};

const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// ── Pre-send transcription editor sizing (Compression box #296, RE-OPENED) ──
//
// AXIS CHOICE: the box only ever changes on the HEIGHT axis. Its WIDTH is owned by
// the composer layout — the segment tray is a full-width column inside the chat
// column, which is itself responsive all the way down to the #243 narrow-split
// floor — so a user-set width would fight that layout and could push content out
// of the bubble. Height is also the axis the complaint is actually about: "too
// narrow" here means "too few visible lines of transcription".
const TRANSCRIPT_MIN_HEIGHT = 48;   // the compact default (~2 lines)
// A floor for the EXPANDED state. "Expand to show the full transcription" is the
// contract, but a one-line transcription's content height is smaller than the
// compact box — so without a floor the toggle would be a visual no-op for exactly
// the short clips users record most. This keeps the toggle unambiguously BINARY:
// compact (48) ↔ clearly-expanded (≥120), growing with the content beyond that.
const TRANSCRIPT_EXPANDED_MIN_HEIGHT = 120;
// The box lives inside the segment tray's own `overflow-y-auto` scroller, so the
// tray's cap IS its maximum useful vertical capacity — past that the tray simply
// scrolls, and #243 containment still holds because the box scrolls internally.
// Deriving the ceiling from the tray keeps it meaningful rather than arbitrary.
const TRANSCRIPT_MAX_HEIGHT = 240;

/**
 * The editable pre-send transcription box with its ALWAYS-VISIBLE expand/collapse
 * toggle. Shared by the Main Office AND the Telegram Bucket Day composers
 * — both render this same <ChatInput>, so both get the behaviour identically.
 *
 * Compression box (#296) — RE-OPENED, and this SUPERSEDES the v1 behaviour:
 *   • v1 shipped a hover-revealed drag handle plus an automatic snap-back the
 * moment interaction left the box. The PO rejected both: the grip was
 * unintuitive ("weird"), and the snap-back was too aggressive — a click
 * anywhere else silently threw the size away.
 *   • v2 is ONE persistent binary toggle: a clean arrow pinned to the box's
 * top-right corner, visible at ALL times (never hover- or focus-gated).
 * Click it while compact → the box expands to show the full transcription;
 * click it again → back to compact. Nothing else on the page ever changes the
 * size — in particular, clicking outside does NOT resize it.
 *   • Edits survive toggling for free, because the text is owned by the parent's
 * segment state and only the height lives here.
 *
 * This also retires the arrow box (#313) v1 threshold logic: the arrow used to
 * appear only past 40% expansion (it had to, because there was no other way to
 * reach a size); with a binary toggle there is no partial state to threshold, so
 * the arrow is simply always there.
 *
 * NOTE: native `resize: vertical` is still deliberately NOT used — it paints a
 * browser-owned grip, i.e. exactly the drag affordance this re-open removes.
 */
const TranscriptEditor = ({ value, isEdited, onChange, placeholder }) => {
    const { t } = useLanguage();
    const wrapperRef = useRef(null);
    const textareaRef = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const [contentHeight, setContentHeight] = useState(TRANSCRIPT_EXPANDED_MIN_HEIGHT);

    // Expanding grows the box downward, so under pressure the newly-revealed lines
    // — and the toggle itself — can fall outside the tray's scrollport. Re-frame it
    // after every expand (#313 2nd re-open, Issue 2: the control must stay reachable
    // when the composer is capped and the tray is scrolling). Collapsing shrinks the
    // box and can never push anything out of view, so it is deliberately not scrolled.
    useEffect(() => {
        if (!expanded) return undefined;
        return scrollTranscriptIntoView(wrapperRef.current);
    }, [expanded]);

    // While expanded, size to the FULL transcription: measure the real content
    // height (clamped to [EXPANDED_MIN, MAX]) and re-measure as the user types, so
    // a growing correction keeps revealing itself until the cap — past which the
    // textarea scrolls internally. useLayoutEffect measures before paint, so the
    // box never flashes at the wrong size.
    useLayoutEffect(() => {
        if (!expanded) return;
        const el = textareaRef.current;
        if (!el) return;
        const restore = el.style.height;
        el.style.height = 'auto'; // collapse first so scrollHeight is the true content height
        const measured = el.scrollHeight;
        el.style.height = restore;
        setContentHeight(Math.min(
            Math.max(measured, TRANSCRIPT_EXPANDED_MIN_HEIGHT),
            TRANSCRIPT_MAX_HEIGHT,
        ));
    }, [expanded, value]);

    const effectiveHeight = expanded ? contentHeight : TRANSCRIPT_MIN_HEIGHT;

    return (
        <div
            ref={wrapperRef}
            data-testid="transcript-editor"
            data-expanded={expanded ? 'true' : 'false'}
            className="relative"
        >
            <textarea
                ref={textareaRef}
                data-testid="composer-voice-transcript"
                data-edited={isEdited ? 'true' : 'false'}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                rows={2}
                placeholder={placeholder}
                style={{ height: `${effectiveHeight}px`, maxHeight: `${TRANSCRIPT_MAX_HEIGHT}px` }}
                /* Transcription Box UI (#292): `ring-inset` is LOAD-BEARING, not
                   cosmetic. A Tailwind ring is a box-shadow painted OUTSIDE the
                   border box, and this textarea is a full-width descendant of the
                   segment tray's capped `overflow-y-auto` scroller — and per
                   CSS, an `overflow-y` other than `visible` computes `overflow-x` to
                   `auto` too, so that scroller clips its padding box on ALL sides.
                   The outset ring therefore lost its left, right and bottom bands,
                   leaving only the top edge plus the two little bottom-corner nooks
                   that fall inside the clip rect — exactly the fragmented border the
                   PO reported. Painting the ring INSIDE the box makes the whole
                   perimeter immune to ancestor clipping, and the matching
                   `focus:border-*` keeps it reading as one continuous frame.
                   Same treatment for the amber "edited" ring, which was clipped
                   identically.
                   `pr-9` reserves the right gutter the toggle arrow sits in, so the
                   control never overlaps the text. It was widened from `pr-6` when
                   the arrow grew to a full 32×32 tap target (#313 2nd re-open). */
                className={`w-full resize-none px-2 pr-9 py-1.5 text-xs rounded border bg-white/[0.05] text-white focus:outline-none ring-inset focus:ring-2 ${
                    isEdited
                        ? 'border-amber-400 ring-1 ring-amber-500/60 focus:ring-amber-400 focus:border-amber-400'
                        /* Focus is the design's white glow, and the ring must be
                           given that colour EXPLICITLY: Tailwind's default
                           `--tw-ring-color` is blue-500, which painted a blue band
                           immediately inside a white border — two colours reading as
                           two different frames. */
                        : 'border-pai focus:border-white/80 focus:ring-white/80'
                }`}
            />

            {/* The one and only sizing control (#296 re-open). ALWAYS rendered and
                always opaque — the PO's spec is "visible at all times", so there is
                deliberately no hover/focus gating here.

                `data-direction` follows the ACCORDION convention (the icon shows
                where the box is about to go): ▼ while compact = "open it downward",
                ▲ while expanded = "close it back up". NOTE this is the OPPOSITE of
                the retired #313 v1 arrow, whose ▼ meant "shrink" — assert on
                `data-expanded` (unambiguous state) rather than on the icon. The PO
                explicitly approved this direction in the #313 2nd re-open, so it is
                UNCHANGED there.

                #313 2nd re-open, Issue 2 ("under pressure the toggle becomes very
                complicated to reach"): the control is now a full 32×32 tap target
                (the icon stays a small 13px chevron — the box grew, not the glyph),
                carries `z-10` so it paints above the textarea and its scrollbar,
                and sits on a near-opaque chip of the textarea's own background so
                text scrolling underneath can never camouflage it. The textarea's
                `pr-9` reserves exactly this gutter, so the two never overlap. */}
            <button
                type="button"
                data-testid="transcript-toggle-arrow"
                data-direction={expanded ? 'up' : 'down'}
                data-expanded={expanded ? 'true' : 'false'}
                /* Never steal focus: toggling the size must not move the caret out
                   of the text the user is editing. Cancelling pointerdown already
                   suppresses the compatibility mousedown in modern engines; the
                   explicit onMouseDown is the belt to that braces. Neither prevents
                   the click itself. */
                onPointerDown={(e) => e.preventDefault()}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setExpanded((v) => !v)}
                className="absolute top-0.5 right-0.5 z-10 h-8 w-8 flex items-center justify-center rounded-md bg-pai-modal/90 text-white/55 hover:text-white hover:bg-white/[0.1] transition-colors"
                title={expanded
                    ? t('voiceTranscriptCollapse', 'Collapse the transcription')
                    : t('voiceTranscriptExpand', 'Show the full transcription')}
                aria-label={expanded
                    ? t('voiceTranscriptCollapse', 'Collapse the transcription')
                    : t('voiceTranscriptExpand', 'Show the full transcription')}
                aria-expanded={expanded}
            >
                {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
        </div>
    );
};

/**
 * One queued audio segment in the input tray. Matching audio files (#139): the
 * pre-send preview now reuses the SAME <VoiceClipPlayer> as the sent chat message
 * (circular play/pause button + deterministic waveform + current/total time)
 * instead of the native <audio controls> "white stick", so the transition from
 * "recording/preview" to "sent message" is visually seamless. It plays inline via
 * the local blob URL while we wait for the server's signed URL, shows the current
 * transcription status under the player, and renders a collapsible "Show
 * transcription" panel once the text is ready.
 *
 * `onEditTranscription` (optional) makes that panel an EDITABLE textarea instead
 * of read-only text — the pre-send transcription editing the Telegram
 * Bucket Day composer ships (#173), and since #252 the Main Office too.
 *
 * Two segment shapes feed that textarea, both handled here:
 *   • bucket — the handler overwrites `transcription` in place (no original kept);
 *   • Main Office (#252) — the handler writes `editedTranscription`, leaving the
 * raw STT in `transcription`, so the sent message can carry the #130/#228
 * "Voice Message N Edited:" contract. A genuine divergence between the two
 * lights up the "Edited" badge + amber ring ("you're editing the draft").
 */
const VoiceSegmentChip = ({ segment, onRemove, onToggleTranscription, onEditTranscription }) => {
    const { t } = useLanguage();
    const playbackUrl = segment.audioUrl || segment.blobUrl;
    const isTranscribing = segment.status === 'transcribing';
    const isReady = segment.status === 'ready';
    const isFailed = segment.status === 'failed';
    const draftTranscript = typeof segment.editedTranscription === 'string'
        ? segment.editedTranscription
        : (segment.transcription || '');
    const isEdited = typeof segment.editedTranscription === 'string'
        && segment.editedTranscription.trim() !== ''
        && segment.editedTranscription.trim() !== (segment.transcription || '').trim();
    // Adapt the composer's segment shape to the shared player's { audio_url,
    // gcs_path, duration } contract. gcsPath is null until the server responds —
    // the player's expired-URL refresh is guarded on gcs_path, so a blob-only
    // preview never triggers a needless refresh.
    const clip = { audio_url: playbackUrl, gcs_path: segment.gcsPath || null, duration: segment.duration };

    // #313 2nd re-open, Issue 1 ("clicking Show Transcription gives no immediate
    // visual feedback"): the revealed block is appended BELOW the player + status
    // row inside the tray's own scroller, so with a capped composer it lands outside
    // the scrollport. Widening the tray (see SEGMENT_TRAY_COLLAPSED_HEIGHT) makes the
    // room; this puts the reveal in front of the user's eyes rather than leaving them
    // to hunt for it. Covers the read-only render as well as the editable one.
    //
    // Deliberately NOT paired with an auto-focus: the reveal is a "let me read this"
    // gesture, and stealing focus would pop the on-screen keyboard on mobile (#171),
    // shrinking the very viewport this fix is protecting.
    const transcriptRef = useRef(null);
    const revealed = isReady && !!segment.showTranscription;
    useEffect(() => {
        if (!revealed) return undefined;
        return scrollTranscriptIntoView(transcriptRef.current);
    }, [revealed]);

    // `data-status` on the chip below mirrors the segment's lifecycle
    // (transcribing | ready | failed) as a structural, language-independent
    // attribute — the status text itself is localised, and #368 needs to assert
    // that a clip whose transcription ran while the shell was unmounted comes
    // back READY rather than stuck on its spinner.
    return (
        <div data-testid="composer-voice-segment" data-status={segment.status} className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                    <VoiceClipPlayer segment={clip} />
                </div>
                <button
                    type="button"
                    onClick={() => onRemove(segment.id)}
                    className="p-1 text-pai-faint hover:text-rose-500 dark:hover:text-rose-400 transition-colors flex-shrink-0"
                    title={t('composerRemoveSegment', 'Remove this segment')}
                >
                    <X size={14} />
                </button>
            </div>

            <div className="px-1 flex items-center justify-between gap-2 text-[11px]">
                {isTranscribing && (
                    <span className="inline-flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
                        <Loader2 size={12} className="animate-spin" />
                        {t('composerTranscribingAudio', 'Transcribing audio…')}
                    </span>
                )}
                {isReady && (
                    <button
                        type="button"
                        onClick={() => onToggleTranscription(segment.id)}
                        className="inline-flex items-center gap-1.5 text-blue-500 dark:text-blue-400 hover:underline"
                    >
                        {segment.showTranscription ? <EyeOff size={12} /> : <Eye size={12} />}
                        {segment.showTranscription
                            ? t('composerHideTranscription', 'Hide transcription')
                            : t('composerShowTranscription', 'Show transcription')}
                    </button>
                )}
                {isReady && isEdited && (
                    <span
                        data-testid="composer-transcript-edited"
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 flex-shrink-0"
                        title={t('composerEditedTitle', 'This transcription was edited — the corrected text is what PAI will act on')}
                    >
                        <Pencil size={10} />
                        {t('composerEdited', 'Edited')}
                    </span>
                )}
                {isFailed && (
                    <span className="text-rose-600 dark:text-rose-400">
                        {t('composerTranscriptionFailed', 'Transcription failed — {reason}', {
                            reason: segment.error || t('composerTranscriptionFailedFallback', 'try removing and re-recording'),
                        })}
                    </span>
                )}
            </div>

            {revealed && (
                <div ref={transcriptRef} data-testid="composer-transcript-block">
                    {onEditTranscription ? (
                        <TranscriptEditor
                            value={draftTranscript}
                            isEdited={isEdited}
                            onChange={(next) => onEditTranscription(segment.id, next)}
                            placeholder={t('voiceTranscriptPlaceholder', 'Transcription — edit before sending')}
                        />
                    ) : (
                        <div className="px-1 text-xs text-pai-secondary whitespace-pre-wrap">
                            {segment.transcription || t('composerSilent', '[silent]')}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const ChatInput = ({
    input,
    setInput,
    attachedFiles,
    onRemoveAttachment,
    onClearAttachments,
    onSendMessage,
    onKeyPress,
    isLoading,
    apiStatus,
    canStop,
    onStopStreaming,
    replyingToMessages = [],
    onCancelReply,
    onRemoveReply,
    onPasteFiles,
    isRecording = false,
    recordingDuration = 0,
    onStartRecording,
    onAcceptRecording,
    onDiscardRecording,
    voiceSegments = [],
    onRemoveVoiceSegment,
    onToggleSegmentTranscription,
    onEditSegmentTranscription, // optional — makes the pre-send transcription editable (#173)
    // #541 Dictation (Main Chat only). With `dictationMode` the mic is a toggle:
    // tap → record, tap again → stop; the parent transcribes and APPENDS the text
    // to `input`. `isDictating` = that transcription is in flight (mic shows
    // "transcribing…", Send waits); `dictationNotice` = `{kind, text}` brief hint
    // ("Didn't catch that" / an error sentence). Other composers (Rulebook, Day
    // channel) leave these off and keep their voice-note behaviour.
    dictationMode = false,
    isDictating = false,
    dictationNotice = null,
    onDismissDictationNotice,
    pendingSend = false,
    referencedExpenses = [],
    onRemoveReferencedExpense,
    onAddFiles,          // (files: File[]) → same pipeline as drag-drop (#208 "+" → Upload from Device)
    onOpenFilePicker,    // () → open the "Select from File Manager" picker (#208)
}) => {
    const { t } = useLanguage();
    const uploadsInFlight = attachedFiles.some(f => f && f.uploading);
    const transcribingCount = voiceSegments.filter(s => s.status === 'transcribing').length;
    const hasAnySegment = voiceSegments.length > 0;
    // Send is available the moment ANY content exists — text, segments
    // (even still-transcribing ones), attachments, or expense refs. When the
    // user clicks while transcriptions are pending, the parent enters
    // `pendingSend` and the button flips to a "Finalizing…" spinner; the
    // parent fires the actual dispatch once everything is ready.
    const canSend = (input.trim() || attachedFiles.length > 0 || referencedExpenses.length > 0 || hasAnySegment)
        && !isLoading
        && apiStatus === 'connected'
        && !isRecording
        && !uploadsInFlight
        && !pendingSend
        && !isDictating;
    // The mic stays visible at all times during normal composition so the
    // user can chain recordings even after typing or accepting a segment.
    // It only hides while actively recording (the recording bar takes over)
    // or while the assistant is mid-stream and the Stop button owns the slot.
    // In dictation mode the mic stays put while recording — it IS the stop
    // control ("tap again") — and while transcribing (spinner, not clickable).
    const showMic = !isLoading
        && !canStop
        && (dictationMode || !isRecording)
        && apiStatus === 'connected';
    const textareaRef = useRef(null);
    // Wraps the textarea — its WIDTH is what a resize needs to re-measure
    // against (#357). See the ResizeObserver effect below `recalcHeight`.
    const composerBoxRef = useRef(null);
    const [needsScroll, setNeedsScroll] = useState(false);
    // "Expanded" = the composer has grown past a single line (multi-line/wrapped
    // text). Drives the "+" button layout: single-line → horizontal row
    // [+][Mic][Send]; expanded → the [+] shifts UP into a centered triangle above
    // the [Mic]/[Send] pair (keeps the text area uncluttered). #208.
    const [isExpanded, setIsExpanded] = useState(false);
    // "Tall" = the text box has grown past 50% of its cap (and stays true AT the
    // cap). Drives the voice tray's collapse to a single scrollable row — the
    // #313-re-open "50% rule". Separate from `isExpanded` above, which fires at the
    // FIRST wrapped line and only moves the "+" button.
    const [isComposerTall, setIsComposerTall] = useState(false);
    // arrow box (#313, 2nd re-open — Issue 1). The 50% collapse YIELDS while the
    // user has a transcription open: a 76px scrollport cannot show the editor that
    // "Show transcription" reveals, so under pressure the click looked like a no-op.
    // With nothing revealed the #313 bound is exactly as before.
    const hasOpenTranscript = voiceSegments.some((s) => s.showTranscription && s.status === 'ready');
    const trayCollapsed = isComposerTall && !hasOpenTranscript;

    // "+" quick-action menu (Upload from Device / Select from File Manager) — #208.
    const plusBtnRef = useRef(null);
    const fileInputRef = useRef(null);
    const [plusMenuOpen, setPlusMenuOpen] = useState(false);
    const [plusMenuRect, setPlusMenuRect] = useState(null);

    // Size the textarea to fit its content, clamped to [MIN, MAX]. Past the cap the
    // box stops growing and reveals an internal scrollbar. This is the single source
    // of truth for the composer height — there is no manual/sticky height to override
    // it, so an empty composer is always exactly one line.
    const recalcHeight = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto'; // collapse first so scrollHeight is the true content height
        const contentHeight = el.scrollHeight;
        const newHeight = Math.min(Math.max(contentHeight, MIN_COMPOSER_HEIGHT), MAX_COMPOSER_HEIGHT);
        el.style.height = `${newHeight}px`;
        setNeedsScroll(contentHeight > newHeight);
        // A comfortable buffer over the single-line height (one wrapped line adds
        // ~24px); anything below it stays "single-line" so a lone caret never
        // trips the triangle layout.
        setIsExpanded(newHeight >= TRIANGLE_MIN_HEIGHT);
        // `newHeight` is already clamped to the cap, so a box sitting AT the cap is
        // always "tall" — the card's "expanded beyond 50% ... or at its maximum".
        setIsComposerTall(newHeight > MAX_COMPOSER_HEIGHT * COMPOSER_TALL_RATIO);
    }, []);

    // Recalculate on every value change: typing, programmatic sets (prompt chips),
    // the clear-on-send (input → ''), and the draft rehydrate on a conversation
    // switch all flow through here, so the box grows/shrinks to match and resets to
    // a single line whenever the content clears. useLayoutEffect runs before paint,
    // so there is no single-line flash on paste.
    useLayoutEffect(() => {
        recalcHeight();
    }, [input, recalcHeight]);

    // ── Placeholder tiers (Ui Clipping, #366) ──────────────────────────────
    //
    // The full placeholder ("Type your message... (Shift+Enter for new
    // line)") is measured against the textarea's OWN live font via an
    // offscreen canvas — the same technique text-fitting libraries use — and
    // swapped for a shorter tier the instant it would no longer fit the
    // composer's real writable width (clientWidth minus its own left/right
    // padding, which includes the ~112px `pr-28` reserved for the mic/send
    // overlay). Without this the browser wraps the sentence onto several
    // lines and the box's fixed one-line height clips everything past the
    // first, reading as the truncated "Type your".
    const placeholderCandidates = useMemo(() => ([
        t('composerPlaceholder', 'Type your message... (Shift+Enter for new line)'),
        t('composerPlaceholderShort', 'Type your message'),
        t('composerPlaceholderMinimal', 'Message...'),
    ]), [t]);
    const measureCanvasRef = useRef(null);
    const measurePlaceholderWidth = useCallback((text) => {
        const el = textareaRef.current;
        if (!el || typeof document === 'undefined') return NaN;
        if (!measureCanvasRef.current) {
            const canvas = document.createElement('canvas');
            measureCanvasRef.current = canvas.getContext && canvas.getContext('2d');
        }
        const ctx = measureCanvasRef.current;
        if (!ctx) return NaN;
        const font = window.getComputedStyle(el).font;
        if (font) ctx.font = font;
        return ctx.measureText(text).width;
    }, []);
    const [placeholderText, setPlaceholderText] = useState(placeholderCandidates[0]);
    const recalcPlaceholder = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;
        const cs = window.getComputedStyle(el);
        const padLeft = parseFloat(cs.paddingLeft) || 0;
        const padRight = parseFloat(cs.paddingRight) || 0;
        const available = el.clientWidth - padLeft - padRight;
        setPlaceholderText(pickPlaceholder(placeholderCandidates, available, measurePlaceholderWidth));
    }, [measurePlaceholderWidth, placeholderCandidates]);
    // Re-pick immediately on mount and whenever the language (and therefore
    // every candidate string) changes — a width-only ResizeObserver wouldn't
    // otherwise notice a same-width language switch.
    useEffect(() => {
        recalcPlaceholder();
    }, [recalcPlaceholder]);

    // Re-measure on a WIDTH change (Input box bug, #357). `recalcHeight` was only
    // ever wired to `input` — so a height set while the composer was NARROW (a
    // feature panel open beside the chat wraps the same text onto more lines)
    // stuck around verbatim once the panel closed and the chat widened back,
    // because nothing told the box to re-measure. The distorted height then
    // persisted until the user sent a message, which clears `input` and — only
    // then — re-triggers the effect above. A ResizeObserver on the box's own
    // wrapper (its width is what wrapping depends on) makes every panel
    // open/close, split-ratio drag, and sidebar toggle self-correcting, with no
    // manual reset. Guarded on WIDTH deltas only: our own `recalcHeight` changes
    // the wrapper's HEIGHT (via the textarea inside it), which would otherwise
    // re-fire this same observer on every recalculation — a still-converging but
    // wasteful loop this callback simply never enters because it ignores it.
    const lastComposerWidthRef = useRef(0);
    useEffect(() => {
        const el = composerBoxRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver((entries) => {
            const entry = entries[0];
            const width = entry?.contentRect?.width ?? el.clientWidth;
            if (Math.abs(width - lastComposerWidthRef.current) < 1) return;
            lastComposerWidthRef.current = width;
            recalcHeight();
            recalcPlaceholder();
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [recalcHeight, recalcPlaceholder]);

    // Paste handling. Image/file paste is uploaded as an attachment (unchanged).
    // For a TEXT paste we also kick an immediate recalc on the next frame: onChange
    // already fires (driving the layout effect above), but scheduling one more pass
    // guarantees the box expands to fit a large pasted block even if React defers
    // the state flush — mirroring the typing auto-expansion exactly.
    const handlePaste = useCallback((e) => {
        const items = e.clipboardData?.items;
        if (items && onPasteFiles) {
            const files = [];
            for (const item of items) {
                if (item.kind === 'file') {
                    const file = item.getAsFile();
                    if (file) files.push(file);
                }
            }
            if (files.length > 0) {
                e.preventDefault(); // Don't paste binary data as text
                onPasteFiles(files);
                return;
            }
        }
        requestAnimationFrame(recalcHeight);
    }, [onPasteFiles, recalcHeight]);

    // ── "+" quick-action menu ────────────────────────────────────────────────
    const togglePlusMenu = useCallback(() => {
        setPlusMenuOpen((open) => {
            if (!open) {
                const el = plusBtnRef.current;
                if (el) setPlusMenuRect(el.getBoundingClientRect());
            }
            return !open;
        });
    }, []);

    // Close the menu on Escape.
    useEffect(() => {
        if (!plusMenuOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setPlusMenuOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [plusMenuOpen]);

    // "Upload from Device" → native OS file picker → SAME ingestion pipeline as
    // drag-drop (Conversation Uploads folder, pills w/ abort, 409 dedup,
    // origin-binding) via onAddFiles.
    const handleUploadFromDevice = useCallback(() => {
        setPlusMenuOpen(false);
        fileInputRef.current?.click();
    }, []);

    const handleNativeFilesChosen = useCallback((e) => {
        const files = [...(e.target.files || [])];
        // Reset so choosing the same file again re-triggers onChange.
        e.target.value = '';
        if (files.length > 0 && onAddFiles) onAddFiles(files);
    }, [onAddFiles]);

    const handleSelectFromFileManager = useCallback(() => {
        setPlusMenuOpen(false);
        onOpenFilePicker?.();
    }, [onOpenFilePicker]);

    // The "+" is available whenever normal composition is possible (connected,
    // not mid-stream). While RECORDING the whole action cluster is hidden — the
    // recording bar owns Discard/Accept — so the "+" hides with mic/send too
    // (simplest + matches #207's invariant). #208.
    const showPlus = apiStatus === 'connected' && !isLoading;

    // The "+" button JSX — rendered in exactly ONE place at a time (inline in the
    // horizontal row when single-line, or as the triangle apex when expanded), so
    // plusBtnRef always points at the live instance.
    const plusButton = (
        <button
            ref={plusBtnRef}
            type="button"
            onClick={togglePlusMenu}
            data-testid="chat-plus-button"
            aria-haspopup="menu"
            aria-expanded={plusMenuOpen}
            aria-label={t('composerAddAttachment', 'Add attachment')}
            className="p-2 text-white/55 hover:text-white transition-colors"
            title={t('composerAddAttachment', 'Add attachment')}
        >
            <Plus size={20} />
        </button>
    );

    return (
        <div className="border-t border-pai-hairline px-[26px] pt-[18px] pb-[22px]">
            <div className="max-w-4xl mx-auto">
                <div className="relative">
                    {/* Attachment tray — capped height with internal scrolling so a
                        huge drop (e.g. a 1,000-file folder) never grows unbounded and
                        covers the input/audio/Send controls (Excess Document Overflow
                        ticket). A batch header with a count + "Clear all" lets the user
                        reset the whole queue in one click instead of removing pills one
                        by one. The input bar below stays pinned regardless of count. */}
                    {attachedFiles.length > 0 && (
                        <div className="mb-1 rounded-ctl border border-pai-subtle bg-white/[0.03]">
                            {attachedFiles.length > 1 && (
                                <div className="flex items-center justify-between px-2.5 pt-1.5 pb-1">
                                    <span className="text-[11px] font-medium text-pai-meta">
                                        {t('composerAttachmentsCount', '{n} attachments', { n: attachedFiles.length })}
                                    </span>
                                    {onClearAttachments && (
                                        <button
                                            type="button"
                                            onClick={onClearAttachments}
                                            className="flex items-center gap-1 text-[11px] font-medium text-pai-meta hover:text-pai-red transition-colors"
                                            title={t('composerRemoveAllAttachments', 'Remove all attachments')}
                                            aria-label={t('composerClearAllAttachments', 'Clear all attachments')}
                                        >
                                            <X size={12} />
                                            {t('composerClearAll', 'Clear all')}
                                        </button>
                                    )}
                                </div>
                            )}
                            <div data-testid="composer-attachments" className="p-2 pt-1 flex flex-wrap gap-2 max-h-[116px] overflow-y-auto">
                                {attachedFiles.map((file, index) => (
                                    <AttachmentPill key={file?.tempId || file?.id || index} file={file} onRemove={onRemoveAttachment} />
                                ))}
                            </div>
                        </div>
                    )}

                    {referencedExpenses.length > 0 && (
                        <div className="px-2 pb-2 pt-1 flex flex-wrap gap-2">
                            {referencedExpenses.map((exp) => (
                                <ExpenseReferenceChip
                                    key={exp.expense_id}
                                    expense={exp}
                                    onRemove={onRemoveReferencedExpense}
                                />
                            ))}
                        </div>
                    )}

                    {/* chat-app-style reply preview (multi-select) */}
                    {replyingToMessages.length > 0 && (
                        <div className="bg-white/[0.05] rounded-t-lg mb-1.5 border-l-4 border-pai-blue">
                            <div className="flex items-center justify-between px-3 pt-2 pb-1">
                                <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                                    {t('composerReplyingTo', 'Replying to {n} message{s}', {
                                        n: replyingToMessages.length,
                                        s: replyingToMessages.length > 1 ? 's' : '',
                                    })}
                                </span>
                                <button
                                    onClick={onCancelReply}
                                    className="flex-shrink-0 p-1 text-pai-faint hover:text-pai-secondary transition-colors"
                                    title={t('composerCancelAllReplies', 'Cancel all replies')}
                                >
                                    <X size={14} />
                                </button>
                            </div>
                            <div className="max-h-[120px] overflow-y-auto px-3 pb-2 space-y-1">
                                {replyingToMessages.map((msg, idx) => (
                                    <div key={msg.dbId || msg.id || idx} className="flex items-center gap-2 py-1 border-t border-pai-hairline first:border-t-0">
                                        <div className={`w-0.5 h-6 rounded-full shrink-0 ${msg.isUser ? 'bg-green-500' : 'bg-purple-500'}`} />
                                        <div className="flex-1 min-w-0">
                                            <span className={`text-[10px] font-semibold ${msg.isUser ? 'text-green-600 dark:text-green-400' : 'text-purple-600 dark:text-purple-400'}`}>
                                                {msg.isUser ? t('composerYou', 'You') : 'PAI'}
                                            </span>
                                            <div className="text-xs text-pai-meta truncate">
                                                {(msg.display || msg.content || '').slice(0, 120)}
                                                {(msg.display || msg.content || '').length > 120 ? '...' : ''}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => onRemoveReply(msg)}
                                            className="flex-shrink-0 p-0.5 text-pai-faint hover:text-pai-secondary transition-colors"
                                            title={t('composerRemoveReply', 'Remove this reply')}
                                        >
                                            <X size={12} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Stacked voice segments — visible during composition, persist
                        across recordings, and only clear when the user sends or
                        removes them individually.

                        arrow box (#313 re-open) points 2 + 3. The clips live in a
                        DEDICATED scroller whose height is capped, so the tray never
                        grows with the number of recordings (3 or 100 look the same),
                        and that cap DROPS to a single audio row the moment the text
                        box passes 50% of its own cap — the two blocks can therefore
                        never add up to a composer that walks off the screen. It stays
                        `overflow-y-auto` in BOTH states, so every clip remains
                        reachable by scrolling; nothing is ever hidden outright.

                        #313 2nd re-open: that collapse is suspended while a
                        transcription is REVEALED (`trayCollapsed`), because a 76px
                        scrollport structurally cannot show the editor the reveal
                        renders — the cause of both reported issues. It is a
                        max-height either way, so the tray still only renders as tall
                        as its content and never exceeds its cap. */}
                    {voiceSegments.length > 0 && (
                        <div data-testid="composer-voice-segments" className="px-1 pb-2">
                            {voiceSegments.length >= 3 && (
                                <div className="px-2 pb-1 text-[10px] font-medium text-emerald-700 dark:text-emerald-400">
                                    {t('composerVoiceMessagesCount', '{n} voice messages', { n: voiceSegments.length })}
                                </div>
                            )}
                            <div
                                data-testid="composer-voice-scroller"
                                data-collapsed={trayCollapsed ? 'true' : 'false'}
                                className="overflow-y-auto space-y-1.5 transition-[max-height] duration-150"
                                style={{
                                    maxHeight: `${trayCollapsed
                                        ? SEGMENT_TRAY_COLLAPSED_HEIGHT
                                        : SEGMENT_TRAY_MAX_HEIGHT}px`,
                                }}
                            >
                                {voiceSegments.map((segment) => (
                                    <VoiceSegmentChip
                                        key={segment.id}
                                        segment={segment}
                                        onRemove={onRemoveVoiceSegment}
                                        onToggleTranscription={onToggleSegmentTranscription}
                                        onEditTranscription={onEditSegmentTranscription}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Active recording — dual-action: ❌ Discard / ✅ Accept.
                        Rendered ABOVE the textarea (NOT replacing it) so the
                        composer keeps its content-based height and the user's typed
                        text stays visible while recording. Accepting closes this bar
                        immediately so the user can keep reading / start another
                        segment without waiting. (First text then audio Box #207.) */}
                    {isRecording && (
                        /* Ui responsive Layout (#243): the bar WRAPS so at the narrowest
                           chat width (split panel expanded) the Discard/Accept controls
                           re-stack onto their own line instead of being clipped/hidden —
                           audio submission must stay reachable at any width. The two
                           buttons are grouped (ml-auto shrink-0) so they always travel and
                           stay fully visible together. */
                        /* Recording box update (#276): restyled to the PO mockup — a
                           NEUTRAL rounded panel (the old rose-tinted "alert" strip is
                           gone), a glowing red dot, a large monospace timer, a muted
                           "Recording..." label, then a ghost/outlined Discard pill and a
                           solid green Accept pill. The mockup shows no waveform, so none
                           was added. The #243 responsive contract is preserved verbatim:
                           the row still WRAPS and the two controls stay grouped
                           (ml-auto shrink-0) so they travel together and remain reachable
                           at the narrowest chat width. */
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 mb-2 bg-white/[0.04] border border-pai-subtle rounded-xl" data-testid="composer-recording-bar">
                            <div
                                data-testid="composer-record-dot"
                                className="w-3 h-3 bg-red-500 rounded-full animate-pulse shrink-0 shadow-[0_0_10px_2px_rgba(239,68,68,0.55)]"
                            />
                            <span
                                data-testid="composer-record-timer"
                                className="text-white dark:text-white font-mono text-lg leading-none tabular-nums tracking-tight shrink-0"
                            >
                                {formatRecordingTime(recordingDuration)}
                            </span>
                            <span className="text-pai-secondary text-base leading-none shrink-0">
                                {t('composerRecordingLabel', 'Recording...')}
                            </span>
                            {/* Ui Clipping (#366): this cluster used to be a rigid
                                `flex items-center gap-3 ml-auto shrink-0` row — the OUTER
                                bar wraps it away from the dot/timer/label as ONE unit
                                (#243), but the cluster itself could never wrap internally,
                                so at the chat's absolute 240px floor its own min-content
                                (Discard + gap + Accept, each with a fixed icon+text pill)
                                exceeded the composer's remaining width and Accept was
                                pushed past the panel's `overflow-hidden` edge — clipped,
                                unclickable. `flex-wrap` here lets Accept drop to its own
                                line under Discard whenever both can't fit side by side;
                                each pill alone is far narrower than the floor, so neither
                                control is ever forced outside the frame. */}
                            <div className="flex flex-wrap items-center justify-end gap-2 ml-auto max-w-full shrink-0">
                                <button
                                    type="button"
                                    onClick={onDiscardRecording}
                                    data-testid="composer-record-discard"
                                    className="flex items-center gap-2 px-4 py-1.5 text-sm text-pai-meta bg-transparent hover:bg-white/[0.07] dark:hover:bg-white/5 hover:text-white border border-pai dark:border-white/15 rounded-full transition-colors shrink-0"
                                    title={t('composerDiscardTitle', 'Discard this recording')}
                                >
                                    <X size={15} className="shrink-0" />
                                    {t('voiceDiscard', 'Discard')}
                                </button>
                                <button
                                    type="button"
                                    onClick={onAcceptRecording}
                                    data-testid="composer-record-accept"
                                    /* `border border-transparent` is not decoration: it
                                       matches Discard's 1px outline so the two pills are
                                       the SAME height, as the mockup renders them. */
                                    className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-500 border border-transparent rounded-full transition-colors shrink-0"
                                    title={dictationMode
                                        ? t('dictationStopTitle', 'Stop and insert the text into your message')
                                        : t('composerAcceptTitle', 'Stop, queue this segment, and keep composing')}
                                >
                                    <Check size={15} className="shrink-0" />
                                    {dictationMode ? t('dictationDone', 'Done') : t('voiceAccept', 'Accept')}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* #541 dictation hint — non-blocking, auto-dismissed by the
                        parent; the textarea below keeps its text and stays usable. */}
                    {dictationMode && dictationNotice && (
                        <div
                            data-testid="dictation-notice"
                            data-kind={dictationNotice.kind}
                            role={dictationNotice.kind === 'error' ? 'alert' : 'status'}
                            className={`flex items-center gap-2 px-3 py-1.5 mb-2 text-[13px] rounded-xl border bg-white/[0.04] ${dictationNotice.kind === 'error' ? 'text-pai-red border-pai-red/40' : 'text-pai-secondary border-pai-subtle'}`}
                        >
                            <span className="flex-1 min-w-0">{dictationNotice.text}</span>
                            {onDismissDictationNotice && (
                                <button
                                    type="button"
                                    onClick={onDismissDictationNotice}
                                    data-testid="dictation-notice-dismiss"
                                    className="p-0.5 text-pai-meta hover:text-white transition-colors shrink-0"
                                    title={t('commonClose', 'Close')}
                                    aria-label={t('commonClose', 'Close')}
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    )}

                    {/* Composer textarea — ALWAYS mounted, even while recording.
                        Because the textarea is never unmounted when the recording
                        state toggles, React preserves the same DOM node and its
                        imperatively-set height, so an expanded (multi-line) box never
                        collapses to a single line mid-recording (#207). While
                        recording, the mic/send actions are hidden — the recording bar
                        above owns Discard/Accept. */}
                    <div className="flex gap-2 items-end">
                        <div ref={composerBoxRef} className="flex-1 relative">
                            <textarea
                                ref={textareaRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyPress={onKeyPress}
                                onPaste={handlePaste}
                                data-testid="chat-composer"
                                placeholder={placeholderText}
                                className={`w-full p-3 pr-32 touch:pr-36 pai-input rounded-bubble resize-none ${needsScroll ? 'overflow-y-auto' : 'overflow-y-hidden'}`}
                                rows="1"
                                style={{
                                    minHeight: `${MIN_COMPOSER_HEIGHT}px`,
                                    maxHeight: `${MAX_COMPOSER_HEIGHT}px`
                                }}
                            />
                            {canStop ? (
                                <button
                                    onClick={onStopStreaming}
                                    data-testid="chat-stop-button"
                                    className="absolute right-2 bottom-2 p-2 text-pai-red hover:brightness-125 transition-[filter]"
                                    title={t('composerStopGenerating', 'Stop generating response')}
                                >
                                    <Square size={20} fill="currentColor" />
                                </button>
                            ) : (!isRecording || dictationMode) ? (
                                // Action cluster. A flex COLUMN anchored bottom-right:
                                //  • single-line → the "+" sits inline as the first
                                // item of the horizontal [+][Mic][Send] row.
                                //  • expanded (multi-line) → the "+" shifts UP as the
                                // apex of a centered triangle above [Mic]/[Send]
                                //    (items-center centers it over the pair). #208.
                                <div className="absolute right-1 bottom-1 flex flex-col items-center gap-1">
                                    {showPlus && isExpanded && plusButton}
                                    <div className="flex items-center gap-0.5 touch:gap-1">
                                        {showPlus && !isExpanded && plusButton}
                                        {showMic && !dictationMode && (
                                            <button
                                                onClick={onStartRecording}
                                                data-testid="chat-mic-button"
                                                className="p-2 text-white/55 hover:text-white transition-colors"
                                                title={t('composerRecordVoice', 'Record voice message')}
                                            >
                                                <Mic size={20} />
                                            </button>
                                        )}
                                        {showMic && dictationMode && (
                                            /* #541: ONE mic, a toggle. idle → tap records;
                                               recording → tap stops and transcribes into
                                               the box; transcribing → spinner, disabled
                                               (the textarea stays editable meanwhile). */
                                            <button
                                                type="button"
                                                onClick={isRecording ? onAcceptRecording : onStartRecording}
                                                disabled={isDictating}
                                                data-testid="chat-mic-button"
                                                data-state={isDictating ? 'transcribing' : (isRecording ? 'recording' : 'idle')}
                                                aria-pressed={isRecording}
                                                className={`p-2 transition-colors ${isRecording
                                                    ? 'text-pai-red hover:brightness-125'
                                                    : isDictating
                                                        ? 'text-white/55 cursor-wait'
                                                        : 'text-white/55 hover:text-white'}`}
                                                title={isDictating
                                                    ? t('dictationTranscribing', 'Transcribing…')
                                                    : isRecording
                                                        ? t('dictationStopTitle', 'Stop and insert the text into your message')
                                                        : t('dictationStartTitle', 'Dictate')}
                                                aria-label={isDictating
                                                    ? t('dictationTranscribing', 'Transcribing…')
                                                    : isRecording
                                                        ? t('dictationStopTitle', 'Stop and insert the text into your message')
                                                        : t('dictationStartTitle', 'Dictate')}
                                            >
                                                {isDictating ? (
                                                    <span data-testid="dictation-transcribing" className="inline-flex">
                                                        <Loader2 size={20} className="animate-spin" />
                                                    </span>
                                                ) : isRecording ? (
                                                    <Square size={18} fill="currentColor" />
                                                ) : (
                                                    <Mic size={20} />
                                                )}
                                            </button>
                                        )}
                                        <button
                                            onClick={onSendMessage}
                                            disabled={!canSend}
                                            data-testid="chat-send-button"
                                            className={`m-1 w-9 h-9 flex items-center justify-center rounded-ctl border-[1.5px] transition-colors ${canSend ? 'border-white/80 bg-white/[0.06] text-white shadow-glow-sm hover:bg-white/[0.12]' : 'border-transparent text-white/30 cursor-not-allowed'}`}
                                            title={pendingSend
                                                ? t('composerFinalizing', 'Finalizing {n} transcription{s}…', {
                                                    n: transcribingCount,
                                                    s: transcribingCount === 1 ? '' : 's',
                                                })
                                                : t('composerSend', 'Send')}
                                        >
                                            {pendingSend ? (
                                                <Loader2 size={20} className="animate-spin" />
                                            ) : (
                                                <Send size={20} />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>

                {/* Hidden native file input for "Upload from Device". A plain
                    <input type="file" multiple> works across desktop + mobile
                    browsers; its files are fed into the SAME pipeline as
                    drag-drop via onAddFiles. #208. */}
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    data-testid="chat-file-input"
                    onChange={handleNativeFilesChosen}
                />
            </div>

            {/* "+" quick-action menu — portaled to <body> so no overflow-hidden
                ancestor can clip it; opens UPWARD (anchored to the "+" button)
                since the composer sits at the bottom of the screen. #208. */}
            {plusMenuOpen && plusMenuRect && createPortal(
                <>
                    <div className="fixed inset-0 z-[59]" onClick={() => setPlusMenuOpen(false)} />
                    <div
                        data-testid="chat-plus-menu"
                        role="menu"
                        className="fixed z-[60] min-w-[220px] py-1 bg-pai-modal border border-pai rounded-ctl shadow-modal-sm"
                        style={{
                            bottom: `${window.innerHeight - plusMenuRect.top + 8}px`,
                            right: `${window.innerWidth - plusMenuRect.right}px`,
                        }}
                    >
                        <button
                            type="button"
                            role="menuitem"
                            data-testid="plus-upload-device"
                            onClick={handleUploadFromDevice}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-white hover:bg-white/[0.07] transition-colors"
                        >
                            <Upload size={16} className="text-pai-faint flex-shrink-0" />
                            {t('attachUploadDevice', 'Upload from Device')}
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            data-testid="plus-select-fm"
                            onClick={handleSelectFromFileManager}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left text-white hover:bg-white/[0.07] transition-colors"
                        >
                            <FolderOpen size={16} className="text-pai-faint flex-shrink-0" />
                            {t('attachFromFileManager', 'Select from File Manager')}
                        </button>
                    </div>
                </>,
                document.body,
            )}
        </div>
    );
};

export default ChatInput;
